import { Component, OnInit, Input, ChangeDetectorRef, Output, EventEmitter, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute } from "@angular/router";
import { DeviceManagementService } from '../device-management.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { SitesService } from 'src/app/sites/sites.service';
import { UsersService } from 'src/app/users/users.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { DeviceTypeService } from 'src/app/home/site-dashboard/device/device-type.service';
import { Observable, of } from 'rxjs';

@Component({
	selector: 'app-manage-device-request',
	templateUrl: './manage-device-request.component.html',
	styleUrls: ['./manage-device-request.component.css']
})
export class ManageDeviceRequestComponent implements OnInit {
	@Input() devicesIds: any;
	@Input() render: string = "full";
	@Output() deviceOwnerSaved = new EventEmitter<void>();
	deviceInfo: any = {};
	customers: any = [];
	sites: any = [];
	customer: any = {};
	site: any = {};
	serialNumbers = [];
	powerStudySiteObj: any = {};
	newCustomer	= {id: 0, customer_name: this.translateService.instant('site.new_customer'), sites: [{'id': 0, 'name': this.translateService.instant('site.new_site')}]};
	newSite		= {id: 0, name: this.translateService.instant('site.new_site')};
	// IoTEmeter devices can only move to an existing customer and site (the backend sends show_new_customer_site: false)
	canAddNewOwner = true;
	originalDeviceSiteId: number;
	currentUser: any = {}; // session user object, untyped across the app

	constructor(
		private deviceManagementService: DeviceManagementService,
		private route: ActivatedRoute,
		private notificationMessage: NotificationMessageService,
		private navigation: NavigationService,
		private sideMenuService: SideMenuService,
		private chRef: ChangeDetectorRef,
		private translateService: TranslateService,
		public sitesService: SitesService,
		public usersService: UsersService,
		private el: ElementRef,
		private renderer: Renderer2,
		private commonService: CommonService,
		public deviceTypeService: DeviceTypeService
	) { }

	ngOnInit() {
		if(this.render == "full") {
			this.sideMenuService.hide();
		}
		this.currentUser = this.usersService.getCurrentUser();
		this.route.params.subscribe(param => {
			this.devicesIds = param.deviceId || '';
			this.devicesIds = this.devicesIds.split(',');
			this.deviceManagementService.getDeviceOwners(this.devicesIds).subscribe(
				data => this.init(data)
			);
		});
		this.site.installation_date = new Date(new Date().setHours(0, 0, 0, 0));
	}

	init(response) {
		this.serialNumbers = response.devices.map(device => device.serial_number);
		this.deviceInfo = response.devices[0];
		this.originalDeviceSiteId = this.deviceInfo.site_id;
		this.powerStudySiteObj = response.power_studies_site;
		this.canAddNewOwner = response.show_new_customer_site !== false;
		this.customers  = response.customersAndSites.sort((item1, item2) => item1.customer_name.toLowerCase() > item2.customer_name.toLowerCase() ? 1 : -1);
		this.prepareDeviceOwners();
	}

	prepareDeviceOwners() {
		// pending customers and sites aren't created yet, so IoTEmeter devices can't move to them either
		const pendingOwners$: Observable<unknown> = this.canAddNewOwner ? this.deviceManagementService.getPendingDeviceOwners() : of([]);
		pendingOwners$.subscribe((res: any) => {
			const pendingCustomers = res.sort((item1, item2) => item1.customer_name.toLowerCase() > item2.customer_name.toLowerCase() ? 1 : -1);
			this.customers = (this.canAddNewOwner ? [this.newCustomer] : []).concat(pendingCustomers, this.customers);

			let deviceSiteCustomerId = 0;

			for(let customer of this.customers){
				if (this.canAddNewOwner)
					customer.sites.unshift(this.newSite);

				for(let site of customer.sites){
					if(site.id == this.deviceInfo.site_id){
						deviceSiteCustomerId = customer.id;

						this.sites = customer.sites.filter((site) => site.is_special !== true).sort((item1, item2) => item1.name.toLowerCase() > item2.name.toLowerCase() ? 1 : -1);
						this.deviceInfo.customer_id = customer.id;
						this.deviceInfo.oldCustomerId = customer.id;
					}
				}
			}
			this.deviceInfo.oldSiteId = this.deviceInfo.site_id;

			this.getCustomerSites(deviceSiteCustomerId, true);
			this.commonService.setPageTabTitle(
				`${this.customers.find(customer => customer.id == this.deviceInfo.customer_id)?.customer_name} - ${this.sites.find(site => site.id == this.deviceInfo.site_id)?.name} - ${this.deviceInfo.serial_number} - ${this.translateService.instant('devices.request_manage_device')}`
			);
			this.selectRequestedNewOwner(this.route.snapshot.queryParams);
		});
	}

	// opened from Manage Device after picking "- New Customer -" / "- New Site -" without the access to create it
	selectRequestedNewOwner(params: {newOwner?: string, customerId?: string}) {
		if (!this.canAddNewOwner)
			return;

		if (params.newOwner == 'customer') {
			this.deviceInfo.customer_id = this.newCustomer.id;
			return this.getCustomerSites(this.newCustomer.id);
		}

		const customerId = Number(params.customerId);
		if (params.newOwner != 'site' || !customerId || this.deviceTypeService.isStudyDevice(this.deviceInfo))
			return;

		if (!this.customers.some(customer => customer.id == customerId))
			return;

		this.deviceInfo.customer_id = customerId;
		this.getCustomerSites(customerId, true);
		this.deviceInfo.site_id = this.newSite.id;
	}

	handlePowerStudySites(customer) {
		const customerStudySite = customer.sites.find(site => site.name === this.commonService.powerStudiesDevicesSiteName) || this.powerStudySiteObj;

		this.sites = [customerStudySite];
		this.site = customerStudySite;
		this.deviceInfo.site_id = this.site.id;
	}

	getCustomerSites(customerId, firstTime = false) {
		this.sites = [];
		this.site.customer_enterprises = [];

		if(!customerId){
			this.sites = this.canAddNewOwner ? [this.newSite] : [];
			this.deviceInfo.site_id = this.canAddNewOwner ? this.newSite.id : null;
			return;
		}
		const hasManagementAccess = this.usersService.hasAccessFunction('device_management');
		const hasManagementSpecialSiteAccess = this.usersService.hasAccessFunction('special_site_device_management');

		for(let customer of this.customers){
			if(customer.id == customerId){
				if (this.deviceTypeService.isStudyDevice(this.deviceInfo))
					return this.handlePowerStudySites(customer);

				if (!this.usersService.hasAccessFunction('super_admin', 'write')) {
					customer.sites = customer.sites.filter((site: any) =>
						(!site.is_special || (site.is_special && hasManagementAccess && hasManagementSpecialSiteAccess)));
				}
				const realSites = customer.sites.filter(site => site.id)
					.sort((item1, item2) => item1.name.toLowerCase() > item2.name.toLowerCase() ? 1 : -1);
				// "- New Site -" stays first whatever the site names sort to (e.g. "(Old) Yard" sorts before "-")
				this.sites = (customer.sites.includes(this.newSite) ? [this.newSite] : []).concat(realSites);

				if (!firstTime) {
					if (realSites.length)
						this.deviceInfo.site_id = realSites[0].id;
					else if (this.sites.length)
						this.deviceInfo.site_id = this.newSite.id;
					else // nothing to pick, don't keep the previous customer's site
						this.deviceInfo.site_id = null;
				}

				break;
			}
		}
	};

	sendManageDeviceRequest() {
		// an empty customer or site means "new" on the server, which IoTEmeter devices can't request
		if (!this.canAddNewOwner && (!this.deviceInfo.customer_id || !this.deviceInfo.site_id))
			return this.notificationMessage.setMessage('translate|g.field_required', {clearOnXTimeNavigate: 1});

		let tempSite = Object.assign({}, this.site);
		let insDate = tempSite.installation_date ? new Date(tempSite.installation_date) : new Date();
		tempSite.installation_date = Math.floor((-insDate.getTimezoneOffset() * 60000 + insDate.getTime()) / 1000);
		if(this.deviceInfo.site_id) {
			delete tempSite.installation_date;
		}

		// PS devices always belong to the customer's "Power Studies Devices" site, the backend resolves it
		const isStudyDevice = this.deviceTypeService.isStudyDevice(this.deviceInfo);

		this.deviceManagementService.sendManageDeviceRequest(
			this.devicesIds,
			this.deviceInfo.customer_id,
			isStudyDevice ? 0 : this.deviceInfo.site_id,
			this.customer,
			isStudyDevice ? {} : tempSite,
			this.deviceInfo.manage_reason,
			{remove_existing: true}
		).subscribe((res: any)=>{
			if (res.api_status) {
				switch(res.api_status) {
					case 1:
						this.notificationMessage.setMessage('globalSuccessMsg');
						return this.goBack();
					case 3:
						const form = this.el.nativeElement;
						const ele = form.querySelectorAll('[name=name]');
						if(ele && ele[0])
							this.renderer.addClass(ele[0], 'invalid-input');

						return this.notificationMessage.setMessage('translate|site.reserved_site_name', {clearOnXTimeNavigate: 1});
					break;
					default:
						return this.notificationMessage.setMessage('globalErrMsg');
					break;
				}
			}
		});
	};

	updateSiteCoords(coords) {
		this.site.latitude = coords.lat;
		this.site.longitude = coords.lng;
		this.site.address = coords.address;
		this.site.country_id = coords.country;
		this.site.state_id = coords.state;
		this.site.city = coords.city;
		this.chRef.detectChanges();
	}

	goBack(){
		return this.navigation.goBack('/');
	}
}
